import { Flex } from '@withjoy/joykit';
import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import type { PropContent } from '../../../../../propValues.types';
import { CarouselImageContainer } from './CarouselImageContainer';
import { CarouselVideoContainer } from './CarouselVideoContainer';
import { CarouselButton } from './CarouselButton.styles';
import { withWindow } from '@shared/utils/withWindow';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useHistory } from '@shared/core';

type ContentCarouselProps = {
  content: PropContent[];
  link?: string;
  label?: string;
  telemetryId?: string;
  autoRotatePeriodMs?: number;
};

export type CarouselSlideBaseProps = {
  asset: PropContent;
  currentPosition: number;
  onClick?: (index: number) => void;
  index: number;
};

const ContentCarousel = (props: ContentCarouselProps) => {
  const { content, telemetryId, link, label, autoRotatePeriodMs } = props;
  const mobile = useIsMobileScreen();
  const [currentPage, setCurrentPage] = useState(0);
  const [hover, setHover] = useState(false);
  const autoRotateTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const { eventHandle } = useEventInfo();
  const history = useHistory();

  const { valuePropLinkInteracted } = useCardTelemetry();

  const clickHandler =
    (link && !label) || (mobile && link)
      ? (slide: number = 0) => {
          valuePropLinkInteracted(telemetryId ?? link, slide);

          if (link.startsWith('/') || link.startsWith('?')) {
            const eventSpecificLink = link.replace(/^\/event\//, `/${eventHandle ?? 'event'}/`);
            history.push(eventSpecificLink);
            return;
          }

          withWindow(global => {
            global.open(link, '_blank');
          });
        }
      : undefined;

  useEffect(() => {
    if (currentPage >= content.length && content.length > 0) {
      setCurrentPage(content.length - 1);
    }
  }, [currentPage, content.length]);

  const nextPage = useCallback(() => {
    setCurrentPage(page => {
      if (page < content.length - 1) {
        return ++page;
      } else {
        return 0;
      }
    });
  }, [content.length]);

  useEffect(() => {
    if (autoRotatePeriodMs && content.length > 1) {
      if (!autoRotateTimerRef.current)
        autoRotateTimerRef.current = setInterval(() => {
          nextPage();
        }, autoRotatePeriodMs);
    }

    return () => {
      autoRotateTimerRef.current && clearTimeout(autoRotateTimerRef.current);
    };
  }, [autoRotatePeriodMs, nextPage, content.length]);

  return (
    <div
      style={{ width: '100%', height: '100%' }}
      onMouseOver={() => setHover(true)}
      onFocus={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {content.map((asset, slideIndex) => {
        const commonProps = {
          key: slideIndex,
          index: slideIndex,
          currentPosition: slideIndex - currentPage,
          onClick: () => clickHandler?.(slideIndex),
        };

        switch (asset.type) {
          case 'image':
            return (
              <CarouselImageContainer
                {...commonProps}
                asset={asset}
              />
            );
          case 'video':
            return (
              <CarouselVideoContainer
                {...commonProps}
                asset={asset}
              />
            );
        }
      })}

      <CarouselNavContainer
        link={link}
        label={label}
        showButton={hover}
      >
        {content.map((_, navIndex) => {
          return (
            <CarouselDot
              key={navIndex}
              index={navIndex}
              active={navIndex === currentPage}
              onSelect={page => {
                setCurrentPage(page);
                autoRotateTimerRef.current && clearInterval(autoRotateTimerRef.current);
              }}
            />
          );
        })}
      </CarouselNavContainer>
    </div>
  );
};

const CarouselNavContainer = ({ children, link, label, showButton: shouldShowButton }: { children?: ReactNode[]; link?: string; label?: string; showButton?: boolean }) => {
  const dotCount = children?.filter(child => child !== null).length ?? 0;
  const [showButton, setShowButton] = useState(shouldShowButton);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    if (shouldShowButton) {
      setShowButton(true);
    }

    if (!shouldShowButton && showButton) {
      timeoutRef.current = setTimeout(() => {
        setShowButton(false);
      }, 400);
    }

    return () => {
      timeoutRef.current && clearTimeout(timeoutRef.current);
    };
  }, [shouldShowButton, showButton]);

  return (
    <Flex
      flexDirection="column"
      bottom="10px"
      width="100%"
      height="max-content"
      position="absolute"
      alignItems="center"
      justifyContent="flex-end"
      gap="10px"
      style={{ zIndex: 5 }}
    >
      {dotCount > 1 && (
        <Flex
          alignItems="center"
          justifyContent="center"
          gap="10px"
          width="100%"
          height="fit-content"
        >
          {children}
        </Flex>
      )}
      {link && label && showButton && (
        <CarouselButton
          shape="rounded"
          showing={shouldShowButton}
          onClick={() => {
            if (link && label) {
              withWindow(global => {
                global.location.href = link;
              });
            }
          }}
        >
          {label}
        </CarouselButton>
      )}
    </Flex>
  );
};

type CarouselDotProps = {
  active?: boolean;
  index: number;
  onSelect?: (index: number) => void;
};

const CarouselDot = (props: CarouselDotProps) => {
  const { active = false, index, onSelect } = props;
  const size = '10px';
  const dotStyle = {
    width: size,
    height: size,
    borderRadius: size,
    backgroundColor: active ? '#595959' : '#EBEBEB',
    opacity: 1,
    cursor: 'pointer',
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      style={dotStyle}
      onClick={() => {
        onSelect?.(index);
      }}
    ></div>
  );
};

export default ContentCarousel;
